import { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import { useFormik } from "formik"
import * as Yup from "yup"

import { useTranslation } from "react-i18next"

import { MoneyAmountInput } from "components/inputs/MoneyAmountInput"

import { usePermissions } from "contexts/PermissionsContext"

import { useIsMounted } from "hooks/useIsMounted"

import { FALLBACK_CURRENCIES } from "constants/currencies"
import { PERMISSIONS } from "constants/permissions"

import { PaymentServiceAdmin } from "services/payment/payment.service"
import { settingsService } from "services/settings/settings.service"
import { userService } from "services/user/user.service"

import { shallowChanged } from "util/shallowChanged"

import { isMicroserviceTenant } from "config/featureFlags"

import { formatFiatCurrency } from "../../util/formatter"
import { InputField } from "../inputs/InputField"

import { Modal } from "./Modal"

Yup.setLocale({
  mixed: {
    required: "Required Field"
  },
  number: {
    min: "Must be greater than or equal to ${min}"
  }
})

interface FormValuesType {
  USD: number
  B_USD: number
  rakeback_balance: number
  affiliate_balance: number
}

interface IProps {
  open: boolean
  data: any
  handler: () => void
}

export const ModalEditBalance = (props: IProps) => {
  const { open, data, handler } = props
  const { t } = useTranslation("translation", { useSuspense: false })
  const isMounted = useIsMounted()
  const { has } = usePermissions()
  const canEditBalance = has(PERMISSIONS.USER_EDIT_BALANCE)
  const canEditRakeback = has(PERMISSIONS.USER_EDIT_RAKEBACK)

  // SOFT-535 — only microservice-owned tenants can enter the cash amount in a chosen currency
  // (the backend converts it to the USD cash wallet). Monolith tenants keep the plain USD field
  // and never send `amountCurrency`, so the backend behaves exactly as before.
  const isMs = isMicroserviceTenant()
  // The currency the admin is entering the cash amount in. "USD" is only the pre-resolution
  // placeholder — the real default is picked below, once the tenant's enabled currencies are known.
  const [amountCurrency, setAmountCurrency] = useState<string>("USD")
  const [enabledCurrencies, setEnabledCurrencies] = useState<string[]>([])
  // Memoized: the default-resolution effect below depends on this list, and a fresh array on every
  // render would re-run it — re-clobbering the admin's manual pick on every keystroke.
  const currencyOptions = useMemo(() => (enabledCurrencies.length > 0 ? enabledCurrencies : FALLBACK_CURRENCIES), [enabledCurrencies])
  // The player's display wallet coin — surfaced so an admin can pick the SAME coin and assign 1:1
  // without an FX round-trip, or deliberately enter another currency and let the backend convert.
  const walletCoin: string | undefined = data?.wallet_coin
  // COIN-per-1-USD rates (base USD), used to (a) show the player's CASH balance in their native coin
  // and (b) preview the USD the entered amount converts to. Mirrors what the backend converts with.
  const [rates, setRates] = useState<Record<string, number> | null>(null)
  const rateOf = (coin?: string): number => (coin && coin !== "USD" ? rates?.[coin] ?? 0 : 1)
  // The USD cash balance the modal edits (cents). null when the balance authority is unavailable.
  const usdCashCents: number | null = data?.coins?.USD ?? null
  // Same balance in the player's wallet coin (cents). Prefer the EXACT figure the backend derived
  // from FS-native (no FX round-trip); fall back to USD × rate only when the backend didn't supply
  // it (older API / non-MS) or rates haven't loaded. native = USD × (coin per USD).
  const nativeCashCents = ((): number | undefined => {
    const exact = data?.cash_native_cents
    if (exact !== null && exact !== undefined) return exact
    if (usdCashCents !== null && walletCoin && rateOf(walletCoin) > 0) {
      return Math.round(usdCashCents * rateOf(walletCoin))
    }
    return undefined
  })()

  const seedCashCents = ((): number => {
    if (!isMs) return usdCashCents ?? 0
    if (amountCurrency === walletCoin && nativeCashCents !== undefined) return nativeCashCents
    if (amountCurrency === "USD") return usdCashCents ?? 0
    const rate = rateOf(amountCurrency)
    return usdCashCents !== null && rate > 0 ? Math.round(usdCashCents * rate) : 0
  })()

  // Load the tenant's ENABLED currencies (settings `rcoin`) for the picker. MS tenants only —
  // monolith tenants never render the picker. Graceful on failure (fallback list).
  useEffect(() => {
    if (!isMs) return
    let cancelled = false
    settingsService
      .getAll()
      .then((settings) => {
        if (cancelled) return
        const rcoin = (settings as any)?.rcoin ?? {}
        setEnabledCurrencies(
          Object.entries(rcoin)
            .filter(([, on]) => on)
            .map(([code]) => code)
        )
      })
      .catch(() => {
        if (!cancelled) setEnabledCurrencies([])
      })
    return () => {
      cancelled = true
    }
  }, [isMs])

  // Load COIN-per-1-USD rates (base USD) for the native-balance readout + USD-equivalent preview.
  // MS tenants only. Graceful on failure (null → the modal simply omits the native/USD hints and
  // the backend still does the authoritative conversion on save).
  useEffect(() => {
    if (!isMs) return
    let cancelled = false
    PaymentServiceAdmin.getCurRates("USD")
      .then((res) => {
        if (!cancelled) setRates(res?.rates ?? null)
      })
      .catch(() => {
        if (!cancelled) setRates(null)
      })
    return () => {
      cancelled = true
    }
  }, [isMs])

  useEffect(() => {
    if (!isMs) return
    const preferred = [walletCoin, "USD"].find((c) => !!c && currencyOptions.includes(c)) ?? currencyOptions[0]
    if (preferred) setAmountCurrency(preferred)
  }, [isMs, currencyOptions, walletCoin, data?.id])

  const validationSchema = Yup.object().shape({
    USD: canEditBalance ? Yup.number().required().min(0) : Yup.number().required().max(0),
    B_USD: canEditBalance ? Yup.number().required().min(0) : Yup.number().required().max(0),
    rakeback_balance: canEditRakeback ? Yup.number().required().min(0) : Yup.number().max(0),
    affiliate_balance: canEditBalance ? Yup.number().required().min(0) : Yup.number().max(0)
  })

  const formik = useFormik<FormValuesType>({
    initialValues: {
      USD: seedCashCents,
      B_USD: data?.coins?.B_USD ?? 0,
      rakeback_balance: data?.rakeback_balance ?? 0,
      affiliate_balance: data?.affiliate_balance ?? 0
    },
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }): Promise<void> => {
      try {
        if (!formik.dirty) return
        const changedValues = shallowChanged(values, formik.initialValues)

        const { USD, B_USD, rakeback_balance, affiliate_balance } = changedValues
        const balancePayload = { ...(USD !== undefined && { USD }), ...(B_USD !== undefined && { B_USD }) }
        const rakebackPayload = {
          ...(rakeback_balance !== undefined && { rakeback_balance }),
          ...(affiliate_balance !== undefined && { affiliate_balance })
        }

        if (Object.keys(balancePayload).length > 0) {
          // Tag the cash amount with the currency it was entered in whenever an MS tenant changed
          // it. A plain "USD" tag is treated by the backend exactly like an untagged body, so there
          // is no special case left to get wrong — omitting the tag is what made a CLP amount land
          // as USD, i.e. the FX rate over the intended credit (~950x for CLP).
          const withCurrency = isMs && USD !== undefined ? { ...balancePayload, amountCurrency } : balancePayload
          await userService.editUserBalance(data.id, withCurrency)
        }

        if (Object.keys(rakebackPayload).length > 0) {
          await userService.editUser(data.id, rakebackPayload)
        }

        if (!isMounted()) return

        toast.success("Successfully updated balance")
        handler()
        resetForm()
      } catch (err: any) {
        toast.error(err?.data?.message ?? err?.message ?? "Failed")
      }
    },
    validationSchema
  })

  return (
    <Modal open={open} handler={handler}>
      <div className="relative flex flex-col justify-between modal-box">
        <div className="flex flex-col">
          {isMs && walletCoin && (
            <div className="mb-3 rounded-md bg-[rgba(132,0,168,0.12)] px-3 py-2 text-sm">
              <div>
                {t("balance.userWalletCoin")} <span className="font-semibold">{walletCoin}</span>
              </div>
              <div className="mt-1">
                {t("balance.currentCashBalance")}{" "}
                <span className="font-semibold">
                  {nativeCashCents !== undefined
                    ? formatFiatCurrency(nativeCashCents, "—", walletCoin)
                    : formatFiatCurrency(usdCashCents ?? undefined, "—", "USD")}
                </span>
                {nativeCashCents !== undefined && walletCoin !== "USD" && (
                  <span className="opacity-70"> ({formatFiatCurrency(usdCashCents ?? undefined, "—", "USD")})</span>
                )}
              </div>
              <div className="mt-1 opacity-70">{t("balance.assignSameCoinHint")}</div>
            </div>
          )}
          {/* MS: the cash field is the multi-part MoneyAmountInput (amount + currency + preview),
              so it gets its OWN full-width row; B_USD follows on the next row. Monolith keeps the
              original side-by-side USD + B_USD layout. */}
          {isMs ? (
            <>
              <div className="w-full">
                <MoneyAmountInput
                  label={t("balance.cashBalance")}
                  placeholder={t("balance.amountMinorUnits")}
                  currencies={currencyOptions}
                  value={{ amountCents: formik.values.USD, currency: amountCurrency }}
                  onChange={(next) => {
                    formik.setFieldValue("USD", next.amountCents ?? 0)
                    setAmountCurrency(next.currency)
                  }}
                  disabled={!canEditBalance}
                  className="w-full"
                />
                {amountCurrency !== "USD" && rateOf(amountCurrency) > 0 && (
                  <span className="mt-1 block text-xs opacity-70">
                    {t("balance.holdInCurrency", {
                      currency: amountCurrency,
                      amount: formatFiatCurrency(Math.round(formik.values.USD / rateOf(amountCurrency)), "—", "USD")
                    })}
                  </span>
                )}
                {amountCurrency !== "USD" && rates !== null && rateOf(amountCurrency) === 0 && (
                  <span className="mt-1 block text-xs text-error">{t("balance.noFxRateForCurrency", { currency: amountCurrency })}</span>
                )}
              </div>
              <div className="mt-2 w-full">
                <InputField
                  id="B_USD"
                  type="number"
                  title={t("balance.bonus")}
                  value={formik.values.B_USD}
                  onChange={formik.handleChange}
                  altLabelOne={formatFiatCurrency(formik.values.B_USD)}
                  invalid={!!(formik.errors.B_USD && formik.touched.B_USD)}
                  altLabelTwo={formik.touched.B_USD ? formik.errors.B_USD : ""}
                />
              </div>
            </>
          ) : (
            <div className="flex items-center space-x-4">
              <InputField
                id="USD"
                type="number"
                title={t("balance.usd")}
                value={formik.values.USD}
                onChange={formik.handleChange}
                altLabelOne={formatFiatCurrency(formik.values.USD)}
                invalid={!!(formik.errors.USD && formik.touched.USD)}
                altLabelTwo={formik.touched.USD ? formik.errors.USD : ""}
              />
              <InputField
                id="B_USD"
                type="number"
                title={t("balance.bonus")}
                value={formik.values.B_USD}
                onChange={formik.handleChange}
                altLabelOne={formatFiatCurrency(formik.values.B_USD)}
                invalid={!!(formik.errors.B_USD && formik.touched.B_USD)}
                altLabelTwo={formik.touched.B_USD ? formik.errors.B_USD : ""}
              />
            </div>
          )}
          <div className="modal-action flex items-center space-x-4">
            <div className="flex items-center space-x-4">
              <InputField
                id="rakeback_balance"
                type="number"
                title={t("balance.rakebackBalance")}
                value={formik.values.rakeback_balance}
                onChange={formik.handleChange}
                altLabelOne={formatFiatCurrency(formik.values.rakeback_balance)}
                invalid={!!(formik.errors.rakeback_balance && formik.touched.rakeback_balance)}
                altLabelTwo={formik.touched.rakeback_balance ? formik.errors.rakeback_balance : ""}
              />
              <InputField
                id="affiliate_balance"
                type="number"
                title={t("balance.affiliateBalance")}
                value={formik.values.affiliate_balance}
                onChange={formik.handleChange}
                altLabelOne={formatFiatCurrency(formik.values.affiliate_balance)}
                invalid={!!(formik.errors.affiliate_balance && formik.touched.affiliate_balance)}
                altLabelTwo={formik.touched.affiliate_balance ? formik.errors.affiliate_balance : ""}
              />
            </div>
          </div>
          <div className="modal-action flex items-center space-x-4 ml-4">
            <button className="btn btn-secondary flex-auto" onClick={handler}>
              {t("common.cancel")}
            </button>
            <button className="btn btn-primary flex-auto" onClick={formik.submitForm}>
              {t("common.save")}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  )
}
